import React from 'react';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Card,
	CardContent,
	Chip,
	Grid,
	Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { QuotationFlightInfo } from '../../types';
import { FlightSegmentMobile } from './FlightSegmentMobile';
import useBreakpoints from 'features/common/hooks/useBreakpoints';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import moment from 'moment';
import { formatLayoverDuration, isoDateToFullDate, layoverBetweenSegments } from '../../helpers';

const useStyles = makeStyles((theme) => ({
	card: { marginTop: theme.spacing(1), paddingLeft: '0', paddingRight: '0', minHeight: '100%' },
	summaryCard: {
		borderRadius: 10,
		border: '1px solid rgba(49, 25, 75, 0.10)',
		boxShadow: '0 6px 18px rgba(49, 25, 75, 0.08)',
		overflow: 'hidden',
		marginBottom: theme.spacing(1.25),
	},
	airport: {
		fontSize: '22px',
		fontWeight: 800,
		color: theme.palette.primary.main,
		lineHeight: 1,
	},
	hour: {
		fontSize: '17px',
		fontWeight: 700,
		color: theme.palette.text.primary,
	},
	line: {
		height: 2,
		width: '100%',
		borderRadius: 999,
		background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
		position: 'relative',
		'&:before': {
			content: '""',
			position: 'absolute',
			left: 0,
			top: -4,
			width: 10,
			height: 10,
			borderRadius: '50%',
			backgroundColor: theme.palette.primary.main,
		},
		'&:after': {
			content: '""',
			position: 'absolute',
			right: 0,
			top: -4,
			width: 10,
			height: 10,
			borderRadius: '50%',
			backgroundColor: theme.palette.secondary.main,
		},
	},
}));
interface Props {
	flightInfo: QuotationFlightInfo[];
}

interface FlightGroup {
	segments: QuotationFlightInfo[];
}

const LONG_CONNECTION_HOURS = 24;

const getSegmentDateTime = (date: string, hour: string) =>
	moment(`${date} ${hour.substring(0, 5)}`, 'YYYY-MM-DD HH:mm');

const groupFlightSegments = (flightInfo: QuotationFlightInfo[]): FlightGroup[] => {
	if (flightInfo.length === 0) return [];
	const groups: FlightGroup[] = [{ segments: [flightInfo[0]] }];

	for (let index = 1; index < flightInfo.length; index++) {
		const previous = flightInfo[index - 1];
		const current = flightInfo[index];
		const previousArrival = getSegmentDateTime(previous.arrival.date, previous.arrival.hour);
		const currentDeparture = getSegmentDateTime(current.departure.date, current.departure.hour);
		const hoursBetween = currentDeparture.diff(previousArrival, 'hours', true);
		const sameAirport = previous.arrival.airport.code === current.departure.airport.code;
		const isConnection = sameAirport && hoursBetween >= 0 && hoursBetween < LONG_CONNECTION_HOURS;

		if (isConnection) {
			groups[groups.length - 1].segments.push(current);
		} else {
			groups.push({ segments: [current] });
		}
	}

	return groups;
};

const stopText = (segments: QuotationFlightInfo[]) => {
	const stops = segments.length - 1;
	if (stops === 0) return 'Directo';
	if (stops === 1) return '1 escala';
	return `${stops} escalas`;
};

const airlinesText = (segments: QuotationFlightInfo[]) =>
	Array.from(new Set(segments.map((segment) => segment.airline.name))).join(' + ');

const connectionText = (segments: QuotationFlightInfo[]) => {
	// Directo: el chip de arriba ya lo dice; repetirlo acá quedaba apretado.
	if (segments.length <= 1) return '';
	return segments
		.slice(0, -1)
		.map((segment) => segment.arrival.airport.code)
		.join(' · ');
};

export const FlightMobile = (props: Props): JSX.Element => {
	const classes = useStyles();
	const { flightInfo } = props;

	const { smallDown } = useBreakpoints();

	// En mobile el carrusel necesita un mínimo; en desktop la card se adapta a
	// su wrapper (forzar 640px la rompía dentro de la grilla envuelta).
	const styles = {
		width: '100%',
		minWidth: smallDown ? '85vw' : 'auto',
	};
	const groups = groupFlightSegments(flightInfo);

	return (
		<Box className={classes.card}>
			<CardContent sx={{ ...styles, p: 1, '&:last-child': { pb: 1 } }}>
				{groups.map((group, groupIndex) => {
					const firstSegment = group.segments[0];
					const lastSegment = group.segments[group.segments.length - 1];
					const totalSegments = group.segments.length;
					return (
						<Card className={classes.summaryCard} key={`${firstSegment.flightNumber}-${groupIndex}`}>
							<CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
								<Box display="flex" justifyContent="space-between" alignItems="center" gap={1} mb={1.25}>
									<Box display="flex" alignItems="center" gap={1} minWidth={0}>
										<FlightTakeoffIcon color="secondary" fontSize="small" />
										<Box minWidth={0}>
											<Typography variant="body2" fontWeight={800} color="primary" noWrap>
												{groupIndex === 0
													? 'Salida'
													: groupIndex === groups.length - 1
													? 'Regreso'
													: `Tramo ${groupIndex + 1}`}
											</Typography>
											<Typography variant="caption" color="text.secondary" fontWeight={600} noWrap>
												{airlinesText(group.segments)}
											</Typography>
										</Box>
									</Box>
									<Chip
										size="small"
										label={stopText(group.segments)}
										sx={{ bgcolor: 'rgba(237, 102, 2, 0.10)', color: 'secondary.main', fontWeight: 800 }}
									/>
								</Box>

								<Grid container alignItems="center" spacing={1}>
									<Grid item xs={3}>
										<Typography className={classes.airport}>{firstSegment.departure.airport.code}</Typography>
										<Typography className={classes.hour}>{firstSegment.departure.hour.substring(0, 5)}</Typography>
									</Grid>
									<Grid item xs={6}>
										<div className={classes.line} />
									</Grid>
									<Grid item xs={3} textAlign="right">
										<Typography className={classes.airport}>{lastSegment.arrival.airport.code}</Typography>
										<Typography className={classes.hour}>{lastSegment.arrival.hour.substring(0, 5)}</Typography>
									</Grid>
								</Grid>

								<Grid container mt={0.75}>
									<Grid item xs={5}>
										<Typography variant="body2" fontWeight={700} color="primary">
											{firstSegment.departure.airport.cityName}
										</Typography>
										{/* La fecha era lo más chico y claro de la card, justo el dato donde
										    equivocarse cuesta un vuelo perdido. Va en color de texto normal
										    y con peso, sin competir con la hora. */}
										<Typography variant="body2" fontWeight={700} color="text.primary">
											{isoDateToFullDate(firstSegment.departure.date)}
										</Typography>
									</Grid>
									<Grid item xs={2} textAlign="center">
										<Typography variant="caption" color="text.secondary" fontWeight={700}>
											{connectionText(group.segments)}
										</Typography>
									</Grid>
									<Grid item xs={5} textAlign="right">
										<Typography variant="body2" fontWeight={700} color="primary">
											{lastSegment.arrival.airport.cityName}
										</Typography>
										<Typography variant="body2" fontWeight={700} color="text.primary">
											{isoDateToFullDate(lastSegment.arrival.date)}
										</Typography>
									</Grid>
								</Grid>
							</CardContent>

							{/* Abierto por defecto: el cliente quiere ver las patas sin tener que clickear. */}
							<Accordion defaultExpanded disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon color="primary" />}
									sx={{
										minHeight: 42,
										bgcolor: 'rgba(49, 25, 75, 0.035)',
										'& .MuiAccordionSummary-content': { my: 1 },
									}}
								>
									<Typography variant="body2" fontWeight={800} color="primary">
										Itinerario completo ({totalSegments} {totalSegments === 1 ? 'vuelo' : 'vuelos'})
									</Typography>
								</AccordionSummary>
								<AccordionDetails sx={{ p: 1, backgroundColor: 'rgba(49, 25, 75, 0.018)' }}>
									{group.segments.map((info, index) => {
										const nextSegment = group.segments[index + 1];
										const layover = nextSegment ? layoverBetweenSegments(info, nextSegment) : null;
										return (
											<React.Fragment key={`${info.flightNumber}-${index}`}>
												<FlightSegmentMobile
													segment={info}
													segmentIndex={index}
													totalSegments={group.segments.length}
													compact
												/>
												{layover && (
													<Box
														sx={{
															my: 0.75,
															py: 0.6,
															px: 1.25,
															borderRadius: '8px',
															border: '1px dashed rgba(49, 25, 75, 0.25)',
															backgroundColor: 'rgba(237, 102, 2, 0.05)',
															textAlign: 'center',
														}}
													>
														<Typography variant="caption" fontWeight={800} color="text.secondary">
															Espera de {formatLayoverDuration(layover.minutes)} en {layover.airportCode}
														</Typography>
													</Box>
												)}
											</React.Fragment>
										);
									})}
								</AccordionDetails>
							</Accordion>
						</Card>
					);
				})}
			</CardContent>
		</Box>
	);
};
